import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import { MoreVert as MoreVertIcon } from '@mui/icons-material';
import sectionColorService from '../../../services/sectionColorService';
import { getStatusIndicator } from '../../../utils/taskStatusIndicator';

/**
 * Helper function to format task titles with proper capitalization
 */
const formatTaskTitle = (title) => {
  if (!title) return '';

  // First, capitalize first letter and lowercase the rest
  let formatted = title.charAt(0).toUpperCase() + title.slice(1).toLowerCase();

  // Then replace known acronyms with their proper capitalization
  formatted = formatted.replace(/\bui\/ux\b/gi, 'UI/UX');
  formatted = formatted.replace(/\bci\/cd\b/gi, 'CI/CD');
  formatted = formatted.replace(/\bci cd\b/gi, 'CI/CD');
  formatted = formatted.replace(/\bapi\b/gi, 'API');
  formatted = formatted.replace(/\bprd\b/gi, 'PRD');
  return formatted;
};

/**
 * Task tile component for displaying a single task
 * @param {Object} props - Component props
 * @param {Object} props.tile - Tile data object containing task details
 * @param {Function} props.onMenuOpen - Function to open the menu
 * @param {Function} props.onClick - Function to handle click on the tile
 * @returns {React.ReactElement} Task tile component
 */
const TaskTile = ({ tile, onMenuOpen, onClick }) => {
  // Get colors from the centralized service
  let { backgroundColor, iconColor } = sectionColorService.getTaskColors({
    task: tile,
    sectionId: tile.sectionId,
    sectionName: tile.sectionName,
    status: tile.status
  });

  return (
    <Box
      sx={{
        height: 140,
        width: '100%',
        maxWidth: { xs: '180px', md: '170px', lg: '160px', xl: '160px' },
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 0,
        overflow: 'hidden',
        position: 'relative',
        backgroundColor: backgroundColor,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        ml: 'auto',
        mr: 'auto',
        mb: 1,
        border: 'none',
        '&:hover': {
          transform: 'translateY(-3px)',
          boxShadow: '0 4px 8px rgba(0,0,0,0.05)'
        }
      }}
      onClick={() => onClick(tile)}
    >
      {/* Status indicator */}
      {tile.status && (
        (() => {
          const statusIndicator = getStatusIndicator(tile.status);

          if (statusIndicator && statusIndicator.icon) {
            return (
              <Box
                sx={{
                  position: 'absolute',
                  top: statusIndicator.position?.top || 10,
                  right: statusIndicator.position?.right || 10,
                  zIndex: 2
                }}
              >
                {statusIndicator.icon}
              </Box>
            );
          }

          return null;
        })()
      )}

      {/* Menu button at bottom right */}
      <Tooltip title="View actions">
        <IconButton
          sx={{
            position: 'absolute',
            bottom: 2,
            right: 2,
            zIndex: 2,
            opacity: 0.7,
            color: '#475969',
            padding: '4px',
            minWidth: '28px',
            minHeight: '28px',
            '&:hover': {
              opacity: 1,
              backgroundColor: 'rgba(0, 0, 0, 0.05)'
            },
          }}
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            onMenuOpen(e, tile);
          }}
        >
          <MoreVertIcon style={{ fontSize: '18px' }} />
        </IconButton>
      </Tooltip>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          width: '100%',
          pt: 4,
          pb: 3.5,
          px: 1.5,
          textAlign: 'center'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            mb: 2,
            height: '60px',
            width: '60px',
            borderRadius: '50%',
            backgroundColor: 'rgba(255,255,255,0.6)',
            color: iconColor
          }}
        >
          {React.cloneElement(tile.icon, {
            style: {
              fontSize: '2.2rem',
              color: 'inherit'
            }
          })}
        </Box>

        <Typography
          variant="body1"
          component="h3"
          sx={{
            fontSize: '1rem',
            fontWeight: 500,
            color: '#475969',
            lineHeight: 1.3,
            mt: 1,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden'
          }}
        >
          {formatTaskTitle(tile.title)}
        </Typography>
      </Box>
    </Box>
  );
};

TaskTile.propTypes = {
  tile: PropTypes.object.isRequired,
  onMenuOpen: PropTypes.func.isRequired,
  onClick: PropTypes.func.isRequired
};

export default React.memo(TaskTile);
